/**
 * PricingConfig Component
 * Configures how prices are mentioned in generated content
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Tag, Percent } from 'lucide-react';
import type { PricingConfig as PricingConfigType, PriceInclusion, PriceFormat } from '@/hooks/useContentConfig';

interface PricingConfigProps {
  value: PricingConfigType;
  onChange: (value: PricingConfigType) => void;
  sourceLabel?: string;
}

const PRICE_INCLUSION_OPTIONS: { value: PriceInclusion; label: string; description: string }[] = [
  { value: 'yes', label: 'Always Include', description: 'Show prices in all content types' },
  { value: 'product_pages_only', label: 'Product Pages Only', description: 'Only show prices in product AEO and descriptions' },
  { value: 'no', label: 'Never Include', description: 'Never mention specific prices' },
];

const PRICE_FORMAT_OPTIONS: { value: PriceFormat; label: string; example: string }[] = [
  { value: 'exact', label: 'Exact Prices', example: '"$299", "199.99"' },
  { value: 'range', label: 'Price Ranges', example: '"$200-$400"' },
  { value: 'relative', label: 'Relative Terms', example: '"premium-priced", "budget-friendly"' },
];

const CURRENCY_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (\u20ac)' },
  { value: 'GBP', label: 'GBP (\u00a3)' },
  { value: 'CAD', label: 'CAD (C$)' },
  { value: 'AUD', label: 'AUD (A$)' },
  { value: 'JPY', label: 'JPY (\u00a5)' },
  { value: 'CHF', label: 'CHF (Fr)' },
];

export function PricingConfig({ value, onChange, sourceLabel }: PricingConfigProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            <CardTitle className="text-lg">Pricing Rules</CardTitle>
          </div>
          {sourceLabel && (
            <Badge variant="outline" className="text-xs">
              From: {sourceLabel}
            </Badge>
          )}
        </div>
        <CardDescription>
          Control how prices and promotions are mentioned in content
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Price Inclusion */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Include Prices</Label>
          </div>
          <RadioGroup
            value={value.includePrices}
            onValueChange={(v) => onChange({ ...value, includePrices: v as PriceInclusion })}
            className="space-y-2"
          >
            {PRICE_INCLUSION_OPTIONS.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-3">
                <RadioGroupItem value={opt.value} id={`price-inc-${opt.value}`} />
                <Label htmlFor={`price-inc-${opt.value}`} className="flex flex-col cursor-pointer">
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Price Format - only show if prices are included */}
        {value.includePrices !== 'no' && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Price Format</Label>
            <RadioGroup
              value={value.priceFormat}
              onValueChange={(v) => onChange({ ...value, priceFormat: v as PriceFormat })}
              className="grid grid-cols-1 md:grid-cols-3 gap-3"
            >
              {PRICE_FORMAT_OPTIONS.map((opt) => (
                <div key={opt.value}>
                  <RadioGroupItem
                    value={opt.value}
                    id={`price-fmt-${opt.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`price-fmt-${opt.value}`}
                    className="flex flex-col items-start gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer"
                  >
                    <span className="font-medium">{opt.label}</span>
                    <span className="text-xs text-muted-foreground italic">{opt.example}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Currency Selection - only show if exact prices */}
        {value.includePrices !== 'no' && value.priceFormat === 'exact' && (
          <div className="space-y-2">
            <Label htmlFor="currency" className="text-sm font-medium">Currency</Label>
            <Select
              value={value.priceCurrency || 'auto'}
              onValueChange={(v) => onChange({ ...value, priceCurrency: v })}
            >
              <SelectTrigger id="currency" className="w-[200px]">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Mention Sales */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <Percent className="h-5 w-5 text-orange-500" />
            <div>
              <Label htmlFor="mentionSales" className="text-sm font-medium">
                Mention Sales & Promotions
              </Label>
              <p className="text-xs text-muted-foreground">
                Reference current discounts or promotional pricing when relevant
              </p>
            </div>
          </div>
          <Switch
            id="mentionSales"
            checked={value.mentionSales === true}
            onCheckedChange={(checked) => onChange({ ...value, mentionSales: checked })}
          />
        </div>
      </CardContent>
    </Card>
  );
}
